import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAuth, useUser } from "@clerk/clerk-react";
import { setToken, setUser, logoutUser, fetchRolesByUserId } from "../reduxStore/authSlice";

export default function RolesBootstrapper({ children }) {
  const dispatch = useDispatch();
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const rolesUserId = useSelector((s) => s.auth.rolesUserId);
  const bootstrappedFor = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const delay = (ms) => new Promise((r) => setTimeout(r, ms));
    const getTokenWithRetry = async () => {
      // Clerk can briefly report signed-in while token is not yet available.
      // If we fetch roles without an auth header, some backends return 200 + []
      // which then "locks in" empty roles (rolesUserId is set and we won't refetch).
      let token = await getToken();
      for (let attempt = 0; attempt < 5 && !token; attempt++) {
        await delay(200);
        token = await getToken();
      }
      return token;
    };

    const go = async () => {
      if (!isLoaded || cancelled) return;

      if (!isSignedIn) {
        bootstrappedFor.current = null;
        dispatch(logoutUser());
        return;
      }
      const clerkId = user?.id;
      if (!clerkId) return;
      if (bootstrappedFor.current === clerkId && rolesUserId === clerkId) {
        return;
      }
      const token = await getTokenWithRetry();
      if (cancelled) return;

      // If we still can't get a token, don't fetch roles yet.
      // We'll try again on the next effect run (e.g. after reload/sign-in change).
      if (!token) {
        console.warn("RolesBootstrapper: missing Clerk token; skipping roles fetch");
        return;
      }

      if (token) dispatch(setToken(token));
      const profile = {
        id: clerkId,
        email: user?.primaryEmailAddress?.emailAddress || null,
        firstName: user?.firstName || null,
        lastName: user?.lastName || null,
        imageUrl: user?.imageUrl || null,
        username: user?.username || null,
      };
      dispatch(setUser(profile));
      dispatch(
        fetchRolesByUserId({
          userId: clerkId,
          authToken: token,
          userEmail: profile.email,
          userUsername: profile.username,
        })
      );
      bootstrappedFor.current = clerkId;
    };
    go();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, user?.id, rolesUserId, dispatch, getToken]);

  return children;
}
